<template>
  <div class="statistics-container">
    <div class="page-header">
      <div class="header-title">
        <div class="title-mark"></div>
        <span>应收账款汇总表</span>
      </div>
    </div>

    <div class="search-area">
      <el-date-picker
        v-model="dateRange"
        type="daterange"
        range-separator="至"
        start-placeholder="结算开始日期"
        end-placeholder="结算结束日期"
        value-format="yyyy-MM-dd"
        class="search-input date-picker"
      ></el-date-picker>
      <el-select
        v-model="selectedDemandUnitNames"
        multiple
        filterable
        placeholder="需求方（多选）"
        class="search-input select-multi"
        clearable
      >
        <el-option
          v-for="item in demandUnitNameOptions"
          :key="item"
          :label="item"
          :value="item"
        ></el-option>
      </el-select>
      <el-select
        v-model="selectedProjectNames"
        multiple
        filterable
        placeholder="项目名称（多选）"
        class="search-input select-multi"
        clearable
      >
        <el-option
          v-for="item in projectNameOptions"
          :key="item"
          :label="item"
          :value="item"
        ></el-option>
      </el-select>
      <el-select
        v-model="selectedContractNames"
        multiple
        filterable
        placeholder="合同名称（多选）"
        class="search-input select-multi"
        clearable
      >
        <el-option
          v-for="item in contractNameOptions"
          :key="item"
          :label="item"
          :value="item"
        ></el-option>
      </el-select>
      <el-button type="primary" @click="handleSearch" icon="el-icon-search">搜索</el-button>
      <el-button @click="handleReset" icon="el-icon-refresh">重置</el-button>
      <el-button v-permission="'statistics:accounts-receivable-summary:export'" type="warning" @click="handleExport" icon="el-icon-download" :loading="exportLoading">导出</el-button>
    </div>

    <div class="table-area">
      <el-table
        :data="tableData"
        border
        stripe
        show-summary
        :summary-method="summaryMethod"
        style="width: 100%"
        v-loading="loading"
        :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
      >
        <el-table-column prop="demandUnitNames" label="需求方" min-width="140" align="center" show-overflow-tooltip></el-table-column>
        <el-table-column prop="projectNames" label="项目名称" min-width="220" align="left">
          <template slot-scope="scope">
            <div v-for="name in splitNames(scope.row.projectNames)" :key="name" class="project-name-line">
              {{ name }}
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="contractName" label="合同名称" min-width="160" align="center" show-overflow-tooltip></el-table-column>
        <el-table-column prop="salesAmount" label="销售金额" width="120" align="center">
          <template slot-scope="scope">
            {{ formatAmount(scope.row.salesAmount) }}
          </template>
        </el-table-column>
        <el-table-column prop="settlementAmount" label="结算金额" width="120" align="center">
          <template slot-scope="scope">
            {{ formatAmount(scope.row.settlementAmount) }}
          </template>
        </el-table-column>
        <el-table-column prop="receivedAmount" label="已收款金额" width="120" align="center">
          <template slot-scope="scope">
            {{ formatAmount(scope.row.receivedAmount) }}
          </template>
        </el-table-column>
        <el-table-column prop="receivableBalance" label="应收款余额" width="120" align="center">
          <template slot-scope="scope">
            {{ formatAmount(scope.row.receivableBalance) }}
          </template>
        </el-table-column>
        <el-table-column prop="dueReceivableAmount" label="已到期应收款金额" width="150" align="center">
          <template slot-scope="scope">
            {{ formatAmount(scope.row.dueReceivableAmount) }}
          </template>
        </el-table-column>
        <el-table-column prop="dueReceivableBalance" label="已到期应收款余额" width="150" align="center">
          <template slot-scope="scope">
            {{ formatAmount(scope.row.dueReceivableBalance) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="100" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" size="small" @click="goDetail(scope.row)">明细表</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pagination.currentPage"
          :page-sizes="[10, 20, 30, 50]"
          :page-size="pagination.pageSize"
          layout="prev, pager, next, jumper, sizes, total"
          :total="pagination.total"
        >
        </el-pagination>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api'

export default {
  name: 'AccountsReceivableSummary',
  data() {
    return {
      dateRange: [],
      selectedDemandUnitNames: [],
      selectedProjectNames: [],
      selectedContractNames: [],
      demandUnitNameOptions: [],
      projectNameOptions: [],
      contractNameOptions: [],
      loading: false,
      exportLoading: false,
      tableData: [],
      /** 当前筛选下全部记录的金额合计（后端计算，非当前页） */
      summaryTotals: null,
      pagination: {
        currentPage: 1,
        pageSize: 10,
        total: 0
      }
    }
  },
  created() {
    this.loadFilterOptions()
    this.loadData()
  },
  methods: {
    splitNames(value) {
      return String(value || '').split(',').map(name => name.trim()).filter(Boolean)
    },
    handleSearch() {
      this.pagination.currentPage = 1
      this.loadData()
    },
    handleReset() {
      this.dateRange = []
      this.selectedDemandUnitNames = []
      this.selectedProjectNames = []
      this.selectedContractNames = []
      this.pagination.currentPage = 1
      this.loadData()
    },
    loadFilterOptions() {
      api.statistics.getSettlementSalesSummaryFilterOptions()
        .then(response => {
          if (!response.data?.success) {
            throw new Error(response.data?.message || '加载应收账款查询条件失败')
          }
          const options = response.data.data || {}
          this.demandUnitNameOptions = options.demandUnitNames || []
          this.projectNameOptions = options.projectNames || []
          this.contractNameOptions = options.contractNames || []
        })
        .catch(err => {
          this.$message.error(err.response?.data?.message || err.message || '加载应收账款查询条件失败')
        })
    },
    handleSizeChange(val) {
      this.pagination.pageSize = val
      this.pagination.currentPage = 1
      this.loadData()
    },
    handleCurrentChange(val) {
      this.pagination.currentPage = val
      this.loadData()
    },
    handleExport() {
      this.exportLoading = true
      const params = {}
      if (this.dateRange && this.dateRange.length === 2) {
        params.startDate = this.dateRange[0]
        params.endDate = this.dateRange[1]
      }
      if (this.selectedDemandUnitNames && this.selectedDemandUnitNames.length > 0) {
        params.demandUnitNames = this.selectedDemandUnitNames
      }
      if (this.selectedProjectNames && this.selectedProjectNames.length > 0) {
        params.projectNames = this.selectedProjectNames
      }
      if (this.selectedContractNames && this.selectedContractNames.length > 0) {
        params.contractNames = this.selectedContractNames
      }
      api.statistics.exportAccountsReceivableSummary(params)
        .then(response => {
          const blob = new Blob([response.data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          })
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          const contentDisposition = response.headers['content-disposition']
          let fileName = '应收账款汇总表.xlsx'
          if (contentDisposition) {
            const match = contentDisposition.match(/filename\*?=([^;]+)/i)
            if (match) {
              fileName = decodeURIComponent(match[1].replace(/^UTF-8''/, '').trim())
            }
          }
          link.download = fileName
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)
          this.$message.success('导出成功')
        })
        .catch(err => {
          this.$message.error(err.response?.data?.message || '导出失败')
        })
        .finally(() => {
          this.exportLoading = false
        })
    },
    goDetail(row) {
      const q = {
        contractId: row.contractId,
        contractName: row.contractName || ''
      }
      if (this.dateRange && this.dateRange.length === 2) {
        q.startDate = this.dateRange[0]
        q.endDate = this.dateRange[1]
      }
      this.$router.push({ name: 'AccountsReceivableSummaryDetail', query: q })
    },
    formatAmount(amount) {
      if (amount === undefined || amount === null) return '0.00'
      return Number(amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },
    summaryMethod() {
      const t = this.summaryTotals || {}
      const fmt = k => this.formatAmount(t[k] != null ? t[k] : 0)
      return [
        '合计',
        '',
        '',
        fmt('salesAmountSum'),
        fmt('settlementAmountSum'),
        fmt('receivedAmountSum'),
        fmt('receivableBalanceSum'),
        fmt('dueReceivableAmountSum'),
        fmt('dueReceivableBalanceSum'),
        ''
      ]
    },
    loadData() {
      this.loading = true
      const params = {
        page: this.pagination.currentPage - 1,
        size: this.pagination.pageSize
      }
      if (this.dateRange && this.dateRange.length === 2) {
        params.startDate = this.dateRange[0]
        params.endDate = this.dateRange[1]
      }
      if (this.selectedDemandUnitNames && this.selectedDemandUnitNames.length > 0) {
        params.demandUnitNames = this.selectedDemandUnitNames
      }
      if (this.selectedProjectNames && this.selectedProjectNames.length > 0) {
        params.projectNames = this.selectedProjectNames
      }
      if (this.selectedContractNames && this.selectedContractNames.length > 0) {
        params.contractNames = this.selectedContractNames
      }
      api.statistics.getAccountsReceivableSummary(params)
        .then(response => {
          if (response.data && response.data.success) {
            const data = response.data.data || {}
            this.tableData = data.content || []
            this.pagination.total = data.totalElements || 0
            this.summaryTotals = data.totals || null
          } else {
            this.tableData = []
            this.summaryTotals = null
            this.$message.error(response.data?.message || '获取应收账款汇总表失败')
          }
        })
        .catch(err => {
          this.tableData = []
          this.summaryTotals = null
          this.$message.error(err.response?.data?.message || '获取应收账款汇总表失败')
        })
        .finally(() => {
          this.loading = false
        })
    }
  }
}
</script>

<style lang="scss" scoped>
.statistics-container {
  padding: 20px;
}
.page-header {
  margin-bottom: 20px;
}
.header-title {
  display: flex;
  align-items: center;
  font-size: 18px;
  font-weight: bold;
  color: #303133;
}
.title-mark {
  width: 4px;
  height: 18px;
  background: #409eff;
  margin-right: 10px;
}
.search-area {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 20px;
}
.search-input {
  width: 200px;
}
.select-multi {
  min-width: 200px;
  max-width: 280px;
}
.date-picker {
  width: 300px;
}
.table-area {
  margin-bottom: 20px;
}
.project-name-line {
  line-height: 1.5;
  white-space: normal;
  word-break: break-word;
}
.project-name-line + .project-name-line {
  margin-top: 4px;
  padding-top: 4px;
  border-top: 1px solid #ebeef5;
}
.pagination-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}
::v-deep .el-table th {
  background-color: #f5f7fa !important;
  color: #606266 !important;
  font-weight: 600;
}
::v-deep .el-table__footer-wrapper tbody td {
  background-color: #fafafa !important;
  font-weight: 600;
}
</style>
